import Decimal from "decimal.js";

/**
 * Money is always a decimal, never a JS number. Arithmetic on `Money` values
 * with `+`/`-` does not type-check, which is how the "no float money" rule
 * (plan §14.2, ADR-0003) is enforced at compile time.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

export type Currency = "CRC" | "USD" | "EUR";
export type RoundingMode = "HALF_UP" | "HALF_EVEN" | "DOWN" | "UP";

const MODES: Record<RoundingMode, Decimal.Rounding> = {
  HALF_UP: Decimal.ROUND_HALF_UP,
  HALF_EVEN: Decimal.ROUND_HALF_EVEN,
  DOWN: Decimal.ROUND_DOWN,
  UP: Decimal.ROUND_UP,
};

export type DecimalInput = string | Decimal | bigint | number;

function toDecimal(v: DecimalInput): Decimal {
  if (typeof v === "number") {
    if (!Number.isInteger(v)) {
      throw new TypeError(`Money from a non-integer JS number (${v}) is forbidden; pass a string`);
    }
    return new D(v);
  }
  if (typeof v === "bigint") return new D(v.toString());
  return new D(v);
}

export class Money {
  readonly amount: Decimal;
  readonly currency: Currency;

  private constructor(amount: Decimal, currency: Currency) {
    if (!amount.isFinite()) throw new RangeError("Money amount must be finite");
    this.amount = amount;
    this.currency = currency;
  }

  static of(amount: DecimalInput, currency: Currency = "CRC"): Money {
    return new Money(toDecimal(amount), currency);
  }

  /**
   * Values read from the database. PostgREST serialises NUMERIC as JSON
   * numbers; accept them only when the decimal text round-trips exactly (at
   * most 15 significant digits), otherwise refuse rather than lose precision.
   * Prefer casting to text in the query (`amount::text`).
   */
  static fromDb(v: string | number | null | undefined, currency: Currency = "CRC"): Money {
    if (v === null || v === undefined) return Money.zero(currency);
    if (typeof v === "string") return new Money(toDecimal(v), currency);
    if (!Number.isFinite(v)) throw new RangeError("non-finite amount from database");
    const text = String(v);
    const digits = text.replace(/^-/, "").replace(".", "").replace(/^0+/, "").replace(/e.*$/i, "");
    if (/e/i.test(text) || digits.length > 15) throw new RangeError(`amount ${text} cannot be represented exactly; cast it to text in the query`);
    return new Money(new D(text), currency);
  }

  static zero(currency: Currency = "CRC"): Money {
    return new Money(new D(0), currency);
  }

  static sum(items: readonly Money[], currency: Currency = "CRC"): Money {
    return items.reduce((acc, m) => acc.add(m), Money.zero(currency));
  }

  private same(other: Money): void {
    if (other.currency !== this.currency) {
      throw new TypeError(`Currency mismatch: ${this.currency} vs ${other.currency}`);
    }
  }

  add(other: Money): Money {
    this.same(other);
    return new Money(this.amount.plus(other.amount), this.currency);
  }

  sub(other: Money): Money {
    this.same(other);
    return new Money(this.amount.minus(other.amount), this.currency);
  }

  /** Multiply by a rate or factor (e.g. an IVA rate "0.13"). */
  times(factor: DecimalInput): Money {
    return new Money(this.amount.times(toDecimal(factor)), this.currency);
  }

  dividedBy(divisor: DecimalInput): Money {
    return new Money(this.amount.dividedBy(toDecimal(divisor)), this.currency);
  }

  neg(): Money {
    return new Money(this.amount.negated(), this.currency);
  }

  abs(): Money {
    return new Money(this.amount.abs(), this.currency);
  }

  /** Convert with an explicit exchange rate (units of target per 1 unit of this). */
  convert(rate: DecimalInput, target: Currency): Money {
    return new Money(this.amount.times(toDecimal(rate)), target);
  }

  round(scale = 2, mode: RoundingMode = "HALF_UP"): Money {
    return new Money(this.amount.toDecimalPlaces(scale, MODES[mode]), this.currency);
  }

  max(other: Money): Money {
    this.same(other);
    return this.amount.gte(other.amount) ? this : other;
  }

  min(other: Money): Money {
    this.same(other);
    return this.amount.lte(other.amount) ? this : other;
  }

  isZero(): boolean {
    return this.amount.isZero();
  }
  isNegative(): boolean {
    return this.amount.isNegative() && !this.amount.isZero();
  }
  isPositive(): boolean {
    return this.amount.isPositive() && !this.amount.isZero();
  }
  eq(other: Money): boolean {
    return this.currency === other.currency && this.amount.eq(other.amount);
  }
  cmp(other: Money): number {
    this.same(other);
    return this.amount.cmp(other.amount);
  }

  /** Exact decimal string, suitable for Postgres NUMERIC. */
  toString(): string {
    return this.amount.toFixed();
  }

  toFixed(scale = 2): string {
    return this.amount.toFixed(scale, Decimal.ROUND_HALF_UP);
  }

  toJSON(): { amount: string; currency: Currency } {
    return { amount: this.toString(), currency: this.currency };
  }
}

export function dec(v: DecimalInput): Decimal {
  return toDecimal(v);
}

export { D as Dec };

/** Non-breaking space: a thousands separator must never let "₡46 220,00" wrap mid-number. */
export const THOUSANDS_SEP = "\u00A0";

/** Costa Rican display format: "₡1 234 567,89" (non-breaking space thousands, comma decimals). */
export function formatCRC(m: Money, scale = 2): string {
  const symbol = m.currency === "CRC" ? "₡" : m.currency === "USD" ? "US$" : "€";
  const neg = m.isNegative();
  const [int = "0", frac] = m.abs().toFixed(scale).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS_SEP);
  return `${neg ? "-" : ""}${symbol}${grouped}${frac ? "," + frac : ""}`;
}
